import { Plugin, ButtonView, Command } from 'ckeditor5';

export default class AvatarPlugin extends Plugin {
    static get pluginName() {
        return 'Avatar';
    }

    init() {
        const editor = this.editor;

        // Add the avatar command
        editor.commands.add('insertAvatar', new InsertAvatarCommand(editor));

        // Add the avatar button to the toolbar
        editor.ui.componentFactory.add('insertAvatar', (locale) => {
            const command = editor.commands.get('insertAvatar');
            const buttonView = new ButtonView(locale);

            buttonView.set({
                label: 'Insert Avatar',
                icon: avatarIcon,
                tooltip: true
            });

            // Bind button state to command state
            buttonView.bind('isEnabled').to(command, 'isEnabled');

            // Execute the command when the button is clicked
            this.listenTo(buttonView, 'execute', () => {
                this.openAvatarDialog();
            });

            return buttonView;
        });
    }

    openAvatarDialog() {
        const editor = this.editor;
        
        // Create modal dialog
        const dialog = this.createAvatarDialog();
        document.body.appendChild(dialog);

        // Focus the first input
        const firstInput = dialog.querySelector('input[type="number"]');
        if (firstInput) {
            firstInput.focus();
        }
    }

    createAvatarDialog() {
        const dialog = document.createElement('div');
        dialog.className = 'ck-avatar-dialog-overlay';
        
        // Add theme detection using localStorage (handling both theme formats)
        let lastTheme = null;
        const checkDarkMode = () => {
            const theme = localStorage.getItem('theme');
            // Handle both theme formats: "true"/"false" and "dark"/"light"
            const isDark = theme === "true" || theme === "dark";
            
            // Only update if theme actually changed
            if (lastTheme !== theme) {
                console.log('Avatar Dialog Theme Change:', { 
                    oldTheme: lastTheme, 
                    newTheme: theme, 
                    isDark: isDark,
                    hasClass: dialog.classList.contains('dark-theme')
                });
                lastTheme = theme;
                if (isDark) {
                    dialog.classList.add('dark-theme');
                } else {
                    dialog.classList.remove('dark-theme');
                }
            }
        };
        
        // Set initial theme
        checkDarkMode();
        
        // Listen for theme changes via localStorage (works for other tabs)
        const handleStorageChange = (e) => {
            if (e.key === 'theme') {
                checkDarkMode();
            }
        };
        
        // Periodic check for same-tab theme changes (fallback)
        const themeCheckInterval = setInterval(checkDarkMode, 500);
        
        window.addEventListener('storage', handleStorageChange);
        
        // Store cleanup functions
        dialog._themeListener = () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(themeCheckInterval);
        };
        
        dialog.innerHTML = `
            <div class="ck-avatar-dialog">
                <div class="ck-avatar-dialog-header">
                    <h3>Insert Avatar</h3>
                    <button class="ck-avatar-dialog-close" type="button"></button>
                </div>
                <div class="ck-avatar-dialog-content">
                    <div class="ck-avatar-preview">
                        <h4>Preview</h4>
                        <img id="avatarPreview" src="${this.getAgentAvatarUrl()}" alt="Agent Avatar" style="max-width: 200px; max-height: 200px;">
                    </div>
                    <div class="ck-avatar-controls">
                        <div class="ck-avatar-size-controls">
                            <h4>Size</h4>
                            <div class="size-row">
                                <div class="size-input-group">
                                    <label for="avatarWidth">Width</label>
                                    <input type="number" id="avatarWidth" value="100" min="10" max="500">
                                </div>
                                <div class="size-input-group">
                                    <label for="avatarHeight">Height</label>
                                    <input type="number" id="avatarHeight" value="100" min="10" max="500">
                                </div>
                            </div>
                            <div class="lock-ratio">
                                <input type="checkbox" id="lockRatio" checked>
                                <label for="lockRatio">Lock aspect ratio</label>
                            </div>
                        </div>
                        <div class="ck-avatar-alignment-controls">
                            <h4>Alignment</h4>
                            <div class="ck-avatar-alignment-options">
                                <label><input type="radio" name="alignment" value="none" checked><span>None</span></label>
                                <label><input type="radio" name="alignment" value="left"><span>Left</span></label>
                                <label><input type="radio" name="alignment" value="center"><span>Center</span></label>
                                <label><input type="radio" name="alignment" value="right"><span>Right</span></label>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="ck-avatar-dialog-footer">
                    <button class="ck-avatar-dialog-cancel" type="button">Cancel</button>
                    <button class="ck-avatar-dialog-insert" type="button">Insert Avatar</button>
                </div>
            </div>
        `;

        // Add event listeners
        this.setupDialogEventListeners(dialog);

        return dialog;
    }

    setupDialogEventListeners(dialog) {
        const editor = this.editor;
        const widthInput = dialog.querySelector('#avatarWidth');
        const heightInput = dialog.querySelector('#avatarHeight');
        const lockRatioCheckbox = dialog.querySelector('#lockRatio');
        const preview = dialog.querySelector('#avatarPreview');

        let originalRatio = 1; // Will be set when image loads

        // Set up image ratio when preview loads
        preview.onload = () => {
            originalRatio = preview.naturalWidth / preview.naturalHeight;
            widthInput.value = Math.round(preview.naturalWidth / 2); // Default to half size
            heightInput.value = Math.round(preview.naturalHeight / 2);
        };

        // Handle ratio locking
        widthInput.addEventListener('input', () => {
            if (lockRatioCheckbox.checked) {
                heightInput.value = Math.round(widthInput.value / originalRatio);
            }
        });

        heightInput.addEventListener('input', () => {
            if (lockRatioCheckbox.checked) {
                widthInput.value = Math.round(heightInput.value * originalRatio);
            }
        });

        // Close dialog
        const closeDialog = () => {
            // Clean up theme listener
            if (dialog._themeListener) {
                dialog._themeListener();
            }
            dialog.remove();
        };

        dialog.querySelector('.ck-avatar-dialog-close').addEventListener('click', closeDialog);
        dialog.querySelector('.ck-avatar-dialog-cancel').addEventListener('click', closeDialog);

        // Close on overlay click
        dialog.addEventListener('click', (e) => {
            if (e.target === dialog) {
                closeDialog();
            }
        });

        // Insert avatar
        dialog.querySelector('.ck-avatar-dialog-insert').addEventListener('click', () => {
            const width = parseInt(widthInput.value);
            const height = parseInt(heightInput.value);
            const alignment = dialog.querySelector('input[name="alignment"]:checked').value;
            
            this.insertAvatar(width, height, alignment);
            closeDialog();
        });

        // Handle Enter key
        dialog.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                dialog.querySelector('.ck-avatar-dialog-insert').click();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                closeDialog();
            }
        });
    }

    insertAvatar(width, height, alignment) {
        const editor = this.editor;
        const avatarUrl = this.getAgentAvatarUrl();

        let insertedElement = null;

        editor.model.change(writer => {
            // Create image element
            const imageElement = writer.createElement('imageBlock', {
                src: avatarUrl,
                alt: 'avatar_image',
                width: width + 'px',
                height: height + 'px'
            });

            // Insert the image at current position
            const insertPosition = editor.model.insertContent(imageElement);
            insertedElement = imageElement;
        });

        // Apply alignment after insertion
        if (alignment !== 'none' && insertedElement) {
            // Wait for the next tick to ensure the image is fully inserted and rendered
            setTimeout(() => {
                // First select the inserted image
                editor.model.change(writer => {
                    const selection = writer.createSelection(insertedElement, 'on');
                    writer.setSelection(selection);
                });

                // For CKEditor 5, images use styles instead of text alignment
                // Apply the appropriate image style based on alignment choice
                const imageStyleCommand = editor.commands.get('imageStyle');
                
                if (imageStyleCommand) {
                    let styleValue = null;
                    if (alignment === 'left') {
                        styleValue = 'alignLeft';
                    } else if (alignment === 'right') {
                        styleValue = 'alignRight';
                    } else if (alignment === 'center') {
                        styleValue = 'alignCenter';
                    }
                    
                    if (styleValue) {
                        imageStyleCommand.execute({ value: styleValue });
                    }
                }
                // 'none' means no alignment style (default)
            }, 100); // Increased timeout to ensure proper rendering
        }
    }

    getAgentAvatarUrl() {
        // Match CKEditor 4 behavior: always use the template image
        const editor = this.editor;
        
        // Get the avatar template URL from editor config (same as CKEditor 4)
        const avatarTemplate = editor.config.get('avatarTemplate');
        if (avatarTemplate) {
            return avatarTemplate;
        }

        // Fallback to same template as CKEditor 4
        const templateUrl = (process.env.CLOUDFRONT_URL || '') + '/img/cention-custom-avatar-template-image-should-not-use-for-another-purpose.svg';
        return templateUrl;
    }
}

class InsertAvatarCommand extends Command {
    refresh() {
        const model = this.editor.model;
        const selection = model.document.selection;
        const allowedIn = model.schema.findAllowedParent(selection.getFirstPosition(), 'imageBlock');

        this.isEnabled = allowedIn !== null;
    }
}

// Simple SVG icon for avatar button
const avatarIcon = `<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="7" r="3" fill="currentColor"/>
    <path d="M10 11c-3.5 0-6 2.5-6 5v2h12v-2c0-2.5-2.5-5-6-5z" fill="currentColor"/>
</svg>`; 